import users from '../../../../../assets/icons/ic_users.svg';
import { useDictionary } from '../../../../../utilities/hooks/UseDictionary';
import RelatedExperienceItem from '../../provider-resources/RelatedExperieceItem';
import PropTypes from 'prop-types';
// import addOns from '../../../../../assets/icons/ic_four_circles.svg';
import React, { useMemo } from 'react';
import { DateFormatHelper } from '../../../../../utilities/helpers/DateFormatHelper';
import ProfileImage from '../../../common/ProfileImage';
import PremiumBadge from '../../../common/PremiumBadge';
import { useSubscription } from '../../../../../utilities/hooks/useSubscription';
import { useUpgradeToBusinessPlan } from '../../../../../utilities/hooks/useUpgradeToBusinessPlan';

const ExperienceDetailsPopup = ({
	experience,
	onCancelClicked,
	showActions,
	onEditSlotClicked,
	showSlotDetails,
	onDeleteSlotClicked,
	onCreateBookingClicked,
}) => {
	const dictionary = useDictionary();
	// Creating an offline booking is a paid (subscription) feature — same gate
	// the other "Create new booking" entry points apply. Basic-plan providers
	// get a "Business plan" badge and the button routes them to the upgrade tab.
	const isSubscription = useSubscription();
	const goToUpgrade = useUpgradeToBusinessPlan();

	const onEditClicked = () => {
		onCancelClicked();
		onEditSlotClicked(false);
	};

	const handleOpenDetails = () => {
		showSlotDetails(true);
		onCancelClicked();
		// history.push(`/${NavigatorPaths.calendar.path}?id=${experience.id}`);
	};

	// Closes the popup and opens NewBookingDialog in slot mode for this slot.
	const handleCreateBooking = () => {
		onCancelClicked();
		onCreateBookingClicked();
	};

	// Basic-plan providers: close the popup and send them to the Plan tab.
	const handleUpgrade = () => {
		onCancelClicked();
		goToUpgrade();
	};

	// "+ Create new booking" is only for experience-based slots; resource slots
	// (which carry `experiences`) have no single experience to book against.
	const canShowCreateBooking =
		Boolean(experience?.experience) && Boolean(onCreateBookingClicked);

	const getActions = () => {
		return (
			<div
				className="dialog-title-container"
				style={{ justifyContent: 'flex-end', gap: '12px' }}
			>
				{DateFormatHelper.isFutureDay(experience.start_time) && experience.status.value === 'open' ? (
					<>
						<div
							className="dialog-close-button dialog-edit-button"
							data-testid="edit-button"
							onClick={onEditClicked}
						/>
						<div
							className="dialog-close-button dialog-delete-button"
							data-testid="delete-button"
							onClick={() => onDeleteSlotClicked(false)}
						/>
					</>
				) : null}
				<div
					className="dialog-close-button"
					style={{ position: 'unset' }}
					onClick={onCancelClicked}
				/>
			</div>
		);
	};

	return (
		<div
			onClick={(e) => e.stopPropagation()}
			className="dialog-container dialog-container-narrow"
			style={{ maxHeight: '90%' }}
		>
			{showActions ? (
				getActions()
			) : (
				<div className="dialog-title-container">
					<div className="dialog-close-button" onClick={onCancelClicked} />
				</div>
			)}
			<div
				className="dialog-content-container-with-scroll thin-scrollbar"
				style={{ height: 'unset' }}
			>
				<div className="experience-details-dialog-row">
					<div
						className="experience-details-dialog-color"
						style={{ backgroundColor: experience?.experience?.color, marginTop: '3px' }}
					/>
					<div className="experience-details-dialog-row-content">
						<h3 style={{ lineHeight: '20px' }} data-testid="experience-title">
							{experience?.experience?.title}
						</h3>
						<h4 style={{ fontWeight: 'normal' }}>
							{DateFormatHelper.rangeDayMonthTimeFormat(
								experience.start_time,
								experience.end_time
							)}
						</h4>
						<div
							className="calendar-slot-experience-list-item"
							style={{
								fontSize: '13px',
								lineHeight: '16px'
							}}
						>
							{DateFormatHelper.slotRecurringFormat(
								experience?.setting,
								dictionary
							)}
						</div>
						{experience.experiences ? (
							<>
								<h4 style={{ marginTop: '24px' }}>
									{dictionary.used_in_experiences}
								</h4>
								<div
									className="experience-info-rows-container"
									style={{ height: 'unset' }}
								>
									{experience.experiences.map((experience) => (
										<RelatedExperienceItem key={experience} item={experience} />
									))}
									{!experience.experiences ||
									experience.experiences.length === 0 ? (
										<div className={'text'}>
											{dictionary.no_experiences_yet}
										</div>
									) : null}
								</div>
							</>
						) : (
							<h4 style={{ fontSize: '14px', marginTop: '24px' }}>
								{experience.is_private_tour
									? dictionary.private
									: dictionary.public}
							</h4>
						)}
					</div>
					<div style={{ marginTop: '26px' }}>
						<ProfileImage name={experience?.name} photo={experience?.avatar_url} size={{width: 32, height: 32, fontSize: 13, lineHeight: 16}}/>
					</div> 
				</div>

				{experience.capacity ? (
					<>
						<div
							className="experience-details-dialog-row"
							style={{ marginTop: '24px' }}
						>
							<img src={users} />
							<h4 style={{ fontSize: '14px' }}>{dictionary.participants}</h4>
							<div className="flex-filler" />
							<h4
								style={{
									fontSize: '14px',
									minWidth: '56px'
								}}
							>
								{experience?.booked + '/' + experience?.capacity?.toString()}
							</h4>
						</div>
						{/* <div className='experience-details-dialog-row' style={{marginTop: '24px'}}>
                        <img src={addOns}/>
                        <h4 style={{fontSize: '14px'}}>{dictionary.add_ons}</h4>
                        <div className='flex-filler'/>
                        <h4 style={{fontSize: '14px', minWidth: '56px'}}>0</h4>
                    </div>*/}
					</>
				) : null}
			</div>

			{showActions ? (
				<>
					<div className="dialog-title-divider" />
					<div
						className="dialog-buttons-container"
						style={{ padding: '12px', marginBottom: '0', gap: '16px' }}
					>
						{canShowCreateBooking ? (
							<button
								className={`button plus-button has-premium-badge${
									!isSubscription ? ' button--locked' : ''
								}`}
								style={{ width: '0', flexGrow: '1' }}
								onClick={
									isSubscription ? handleCreateBooking : handleUpgrade
								}
								data-testid="popup-create-booking-button"
							>
								{dictionary.create_new_booking}
								{!isSubscription ? <PremiumBadge /> : null}
							</button>
						) : null}
						<div
							className="secondary-button"
							data-testid="details-button"
							onClick={handleOpenDetails}
						>
							{dictionary.details}
						</div>
					</div>
				</>
			) : null}
		</div>
	);
};

ExperienceDetailsPopup.propTypes = {
	experience: PropTypes.object,
	onCancelClicked: PropTypes.func,
	onEditSlotClicked: PropTypes.func,
	onDeleteSlotClicked: PropTypes.func,
	onCreateBookingClicked: PropTypes.func,
	showActions: PropTypes.bool,
	showSlotDetails: PropTypes.func
};

export default ExperienceDetailsPopup;
